import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { ThemeContext } from '../../contexts/ThemeContext';
import AuthContext from '../../contexts/AuthContext';
import { HomeIcon, HashtagIcon, UserIcon, MoonIcon, SunIcon, Bars3Icon as MenuIcon, XMarkIcon as XIcon, ChatBubbleLeftRightIcon, Cog6ToothIcon as SettingsIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SearchBar from '../search/SearchBar';
import NotificationsDropdown from '../notifications/NotificationsDropdown';
import { useTranslation } from 'react-i18next';

const Navbar: React.FC = () => {
  const [user] = useAuthState(auth);
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { t } = useTranslation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          {/* Mobile Header Layout */}
          <div className="flex md:hidden w-full items-center justify-between">
            {!isSearchOpen ? (
              <>
                {/* Logo */}
                <Link to="/" className="flex items-center">
                  <img src="/logo.png" alt="domired" className="h-8 w-auto mr-2" />
                  <span className="text-xl font-bold text-domired-600">{t('common.appName')}</span>
                </Link>

                <div className="flex items-center space-x-2">
                  {/* Search Icon to toggle expanded search */}
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    <MagnifyingGlassIcon className="h-6 w-6" />
                  </button>

                  {/* Menu Button */}
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {isMenuOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                  </button>
                </div>
              </>
            ) : (
              /* Expanded Search Bar */
              <div className="flex w-full items-center space-x-2">
                <div className="flex-grow">
                  <SearchBar onSearch={() => setIsSearchOpen(false)} />
                </div>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2 text-domired-600 font-medium text-sm"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>

          {/* Desktop Layout (Hidden on Mobile) */}
          <div className="hidden md:flex flex-grow justify-between items-center">
            {/* Logo and brand */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img src="/logo.png" alt="domired" className="h-8 w-auto mr-2" />
                <span className="text-2xl font-bold text-domired-600">{t('common.appName')}</span>
              </Link>
            </div>

            {/* Search Bar - Center */}
            <div className="w-1/3 mx-4">
              <SearchBar />
            </div>

            {/* Desktop Navigation */}
            <div className="flex items-center space-x-4">
              <Link to="/" className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                <HomeIcon className="h-6 w-6" />
              </Link>
              <Link to="/explore" className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                <HashtagIcon className="h-6 w-6" />
              </Link>
              {user && (
                <>
                  <NotificationsDropdown />
                  <Link to="/messages" className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                    <ChatBubbleLeftRightIcon className="h-6 w-6" />
                  </Link>
                  <Link to={`/profile/${user.uid}`} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                    <UserIcon className="h-6 w-6" />
                  </Link>
                  <Link to="/settings" className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                    <SettingsIcon className="h-6 w-6" />
                  </Link>
                </>
              )}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {darkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
              </button>
            </div>

            {/* Auth buttons for desktop */}
            <div className="flex items-center space-x-2 ml-4">
              {user ? (
                <button
                  onClick={handleLogout}
                  className="btn-secondary text-sm"
                >
                  {t('common.logout')}
                </button>
              ) : (
                <>
                  <Link to="/login" className="btn-secondary text-sm">
                    {t('common.login')}
                  </Link>
                  <Link to="/register" className="btn-primary text-sm">
                    {t('common.register')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <HomeIcon className="h-5 w-5 mr-2" />
                <span>{t('home.title')}</span>
              </Link>
              <Link
                to="/explore"
                className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                <HashtagIcon className="h-5 w-5 mr-2" />
                <span>{t('common.search')}</span>
              </Link>
              {user && (
                <>
                  <Link
                    to="/notifications"
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="h-5 w-5 mr-2 flex items-center justify-center">🔔</span>
                    <span>{t('common.notifications')}</span>
                  </Link>
                  <Link
                    to="/messages"
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    <span>{t('common.messages')}</span>
                  </Link>
                  <Link
                    to={`/profile/${user.uid}`}
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <UserIcon className="h-5 w-5 mr-2" />
                    <span>{t('common.profile')}</span>
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <SettingsIcon className="h-5 w-5 mr-2" />
                    <span>{t('common.settings')}</span>
                  </Link>
                </>
              )}
              <button
                onClick={() => {
                  toggleTheme();
                  setIsMenuOpen(false);
                }}
                className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {darkMode ? (
                  <>
                    <SunIcon className="h-5 w-5 mr-2" />
                    <span>{t('settings.lightMode')}</span>
                  </>
                ) : (
                  <>
                    <MoonIcon className="h-5 w-5 mr-2" />
                    <span>{t('settings.darkMode')}</span>
                  </>
                )}
              </button>
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span>{t('common.logout')}</span>
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{t('common.login')}</span>
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{t('common.register')}</span>
                  </Link>
                </>
              )}
              {/* Selector de idioma para móvil */}
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                <div className="flex items-center">
                  <span className="text-sm">{t('settings.otherOptions')}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
